import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { logger } from '@fiverr-private/obs';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cloudflare-turnstile';
const TOKEN_TIMEOUT_MS = 20_000;

interface TurnstileApi {
    render: (container: HTMLElement, params: Record<string, unknown>) => string;
    remove: (widgetId: string) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let scriptPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
    if (window.turnstile) {
        return Promise.resolve();
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const existing = document.getElementById(SCRIPT_ID);
            if (existing) {
                existing.addEventListener('load', () => resolve());
                return;
            }
            const script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = SCRIPT_SRC;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error('Failed to load Turnstile script'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

/**
 * Obtain a one-time Turnstile token. Renders an interaction-only widget (invisible unless
 * Cloudflare decides to challenge). Resolves null on any failure — callers proceed and let
 * the backend decide (it fail-opens when the secret isn't configured).
 */
export const getTurnstileToken = async (siteKey: string | null | undefined): Promise<string | null> => {
    if (!siteKey || typeof window === 'undefined') {
        return null;
    }
    try {
        await loadScript();
        const turnstile = window.turnstile;
        if (!turnstile) {
            return null;
        }
        return await new Promise<string | null>((resolve) => {
            const container = document.createElement('div');
            // Visible-capable slot in case CF escalates to an interactive challenge.
            container.style.cssText = 'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:99999;';
            document.body.appendChild(container);

            // `finish` runs only from the widget callbacks / timeout — always after render() returns.
            const finish = (token: string | null) => {
                try {
                    turnstile.remove(widgetId);
                } catch {
                    // widget already gone
                }
                container.remove();
                resolve(token);
            };
            const timeout = setTimeout(() => finish(null), TOKEN_TIMEOUT_MS);

            const widgetId = turnstile.render(container, {
                sitekey: siteKey,
                appearance: 'interaction-only',
                callback: (token: string) => {
                    clearTimeout(timeout);
                    finish(token);
                },
                'error-callback': () => {
                    clearTimeout(timeout);
                    finish(null);
                },
            });
        });
    } catch (err) {
        logger.warn('[turnstile] token acquisition failed', { err });
        return null;
    }
};

interface TurnstileState {
    /** Resolves a fresh single-use token, or null when captcha is disabled/unavailable. */
    getToken: () => Promise<string | null>;
}

const TurnstileContext = createContext<TurnstileState>({ getToken: async () => null });

export const TurnstileProvider = ({ siteKey, children }: { siteKey?: string | null; children: React.ReactNode }) => {
    const getToken = useCallback(() => getTurnstileToken(siteKey), [siteKey]);
    const value = useMemo(() => ({ getToken }), [getToken]);
    return <TurnstileContext.Provider value={value}>{children}</TurnstileContext.Provider>;
};

export const useTurnstile = (): TurnstileState => useContext(TurnstileContext);
